import { useState } from 'react';
import type { NavItem } from './nav-tree.tsx';
import { pathMatches } from './nav-tree.tsx';

/**
 * Per-group fold state for the sidebar. Composed of:
 *   - pure functions (tested directly in .test.ts)
 *   - thin React hook that wires them into useState
 *
 * Intent preservation: once the user manually collapses the active
 * group, subsequent re-renders will NOT re-expand it. Auto-expand is
 * only re-triggered by a navigation to a different pathname.
 *
 * Why no useEffect: the previous `useEffect([pathname, visibleTree])`
 * re-ran whenever upstream context churned `visibleTree`'s reference,
 * re-expanding the active group and undoing user collapse. Derived-state
 * pattern (setState inside render, guarded by a tracked prop value)
 * runs exactly when `activePathname` changes — no more, no less.
 *
 * Why no callback-form setState for toggle: React StrictMode dev
 * double-invokes state-updater callbacks. If the callback toggles prev,
 * the second invocation un-toggles. Value-form setState avoids that.
 */

export const STORAGE_PREFIX = 'ubc-sidebar-group:';

export interface SidebarStorage {
  read(key: string): string | null;
  write(key: string, value: string): void;
}

export const localStorageAdapter: SidebarStorage = {
  read(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  write(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // storage disabled → state still lives in React, just not persisted
    }
  },
};

// ── Pure functions (testable without React) ──────────────────────────

/** Compute initial open/close map from tree defaults + storage. */
export function computeInitialFoldState(
  tree: readonly NavItem[],
  storage: SidebarStorage,
): Record<string, boolean> {
  const state: Record<string, boolean> = {};
  for (const n of tree) {
    if (n.type !== 'group') continue;
    const stored = storage.read(STORAGE_PREFIX + n.key);
    state[n.key] = stored === null ? n.defaultOpen : stored === '1';
  }
  return state;
}

/** Compute new state after a toggle. Returns { next, newOpen } tuple. */
export function computeToggleResult(
  state: Record<string, boolean>,
  key: string,
): { next: Record<string, boolean>; newOpen: boolean } {
  const newOpen = !(state[key] ?? false);
  return { next: { ...state, [key]: newOpen }, newOpen };
}

/**
 * Compute auto-expand result for a pathname change. Returns a new state
 * if any group should be opened, or `null` if nothing changes (so the
 * caller can avoid a re-render).
 */
export function computeAutoExpand(
  state: Record<string, boolean>,
  tree: readonly NavItem[],
  activePathname: string,
): Record<string, boolean> | null {
  let changed = false;
  const next = { ...state };
  for (const n of tree) {
    if (n.type !== 'group') continue;
    if (n.children.some((c) => pathMatches(c.to, activePathname)) && !next[n.key]) {
      next[n.key] = true;
      changed = true;
    }
  }
  return changed ? next : null;
}

// ── React hook (thin wiring) ─────────────────────────────────────────

export interface SidebarFoldState {
  readonly isOpen: (key: string) => boolean;
  readonly toggle: (key: string) => void;
}

export function useSidebarFoldState(
  tree: readonly NavItem[],
  activePathname: string,
  storage: SidebarStorage = localStorageAdapter,
): SidebarFoldState {
  const [openState, setOpenState] = useState<Record<string, boolean>>(() =>
    computeInitialFoldState(tree, storage),
  );
  const [lastSyncedPath, setLastSyncedPath] = useState<string | null>(null);

  // Derived-state sync: setState inside render, guarded by tracked path.
  // React batches both setState into a single re-render.
  if (lastSyncedPath !== activePathname) {
    setLastSyncedPath(activePathname);
    const expanded = computeAutoExpand(openState, tree, activePathname);
    if (expanded !== null) setOpenState(expanded);
  }

  const toggle = (key: string): void => {
    const { next, newOpen } = computeToggleResult(openState, key);
    storage.write(STORAGE_PREFIX + key, newOpen ? '1' : '0');
    setOpenState(next); // value-form, not callback — StrictMode-safe
  };

  return {
    isOpen: (key) => openState[key] ?? false,
    toggle,
  };
}
